import { getAntiforgeryToken } from "../api/http";
import { shortDateTime } from "../contacts/contactModel";
import { contains } from "../contacts/searchText";

// Respuestas rápidas (QuickReplyContracts.cs, HttpReplyDataSource.cs y las reglas puras de QuickReplies del portal
// actual): los mismos contratos, la misma dirección de la API y las mismas validaciones antes de guardar. El servidor
// decide el permiso (CanManage, CanManageCompany, bandejas que se pueden administrar) y vuelve a validar todo.

export type ReplyVariable = {
  key: string;
  label: string;
  kind: string;
  required: boolean;
  fallback?: string | null;
  options?: string[] | null;
};
export type ReplyFile = { id: string; name: string; contentType: string; bytes: number };
export type ReplyModel = {
  id: string;
  organizationId: number;
  ownerUserId: number;
  scope: string;
  inboxId: number | null;
  name: string;
  shortcut: string;
  content: string;
  state: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  variables: ReplyVariable[];
  attachmentIds: string[];
  literalContent: boolean;
};
export type ReplyView = {
  reply: ReplyModel;
  canManage: boolean;
  favorite: boolean;
  sendsLast30Days: number | null;
  lastUsedAt: string | null;
  lastUsedByActorAt: string | null;
  files: ReplyFile[];
};
export type ReplyLibrary = {
  items: ReplyView[];
  total: number;
  canManageCompany: boolean;
  manageableInboxes: number[];
  failureCode: string | null;
  inboxNames: Record<string, string> | null;
  snapshotId: string | null;
  snapshotExpired: boolean;
};
export type ReplySave = {
  actionId: string;
  name: string;
  shortcut: string;
  content: string;
  scope: string;
  inboxId: number | null;
  expectedVersion: number;
  variables: ReplyVariable[] | null;
  attachmentIds: string[] | null;
  state: string;
  literalContent: boolean;
};
export type ReplyResult = {
  value: ReplyView | null;
  failureCode: string | null;
  errors: Record<string, string> | null;
};
export type ReplyRequest = {
  id?: string | null;
  input?: ReplySave | null;
  actionId?: string;
  version?: number;
  value?: boolean;
};
export type ChatBootstrap = {
  organizationId: number;
  userId: number;
  locale: string;
  people: { userId: number; displayName: string }[];
  labels: Record<string, string>;
  organizationName: string;
  actorDisplayName: string;
};

/** Textos del chat (ChatLabels): una clave sin texto se ve como ⟦CLAVE⟧. */
export type Labels = { (key: string): string; format(key: string, ...values: (string | number)[]): string };
export function chatLabels(values: Record<string, string>): Labels {
  const get = ((key: string) => (values[key] ? values[key]! : `⟦${key}⟧`)) as Labels;
  get.format = (key, ...args) =>
    get(key).replace(/\{(\d+)\}/g, (match, index: string) => String(args[Number(index)] ?? match));
  return get;
}

export const newId = () => crypto.randomUUID();

const tokens = /\{\{\s*([a-z][a-z0-9_]*)\s*\}\}/g;
const automatic = (key: string) => key === "nombre" || key === "agente" || key === "empresa";

/** Variables usadas por el mensaje, incluidas las automáticas sin metadatos (ReplyUsedVariables). */
export function usedVariables(
  content: string,
  definitions: ReplyVariable[],
  literal: boolean,
  L: Labels,
): ReplyVariable[] {
  if (literal) return [];
  const keys = [...new Set([...content.matchAll(tokens)].map((match) => match[1]!))];
  return keys.map(
    (key) =>
      definitions.find((item) => item.key === key) ?? {
        key,
        label: L("REPLY_AUTO_" + key.toUpperCase()),
        kind: "AUTO",
        required: true,
      },
  );
}

/** Errores de variables junto a su campo (ReplyVariableErrors). */
export function variableErrors(content: string, variables: ReplyVariable[], literal: boolean): Record<string, string> {
  const errors: Record<string, string> = {};
  if (literal) return errors;
  const remainder = content.replace(tokens, "");
  if (remainder.includes("{{")) errors.content = "A1_TOKEN_UNCLOSED";
  else if (remainder.includes("}}")) errors.content = "A1_TOKEN_INVALID";
  const keys = [...new Set([...content.matchAll(tokens)].map((match) => match[1]!))];
  for (const variable of variables) {
    const prefix = "variable-" + variable.key + "-";
    if (!variable.label?.trim()) errors[prefix + "label"] = "A1_LABEL_REQUIRED";
    else if (variable.label.length > 100) errors[prefix + "label"] = "A1_LABEL_LONG";
    if ((variable.fallback?.length ?? 0) > 500) errors[prefix + "fallback"] = "A1_FALLBACK_LONG";
    if (variable.kind === "CHOICE" && (!variable.options?.length || variable.options.some((option) => !option.trim())))
      errors[prefix + "options"] = "A1_OPTIONS_REQUIRED";
    if (!["AUTO", "TEXT", "NUMBER", "DATE", "CHOICE"].includes(variable.kind))
      errors[prefix + "type"] = "A1_TYPE_INVALID";
    if (!keys.includes(variable.key) || (variable.kind === "AUTO" && !automatic(variable.key)))
      errors.content = "A1_TOKEN_INVALID";
  }
  if (
    new Set(variables.map((variable) => variable.key)).size !== variables.length ||
    keys.some((key) => !automatic(key) && variables.every((variable) => variable.key !== key))
  )
    errors.content = "A1_TOKEN_INVALID";
  return errors;
}

/** Partes del mensaje: texto y variables como etiqueta o con su ejemplo (ReplyMessageParts). */
export function messageParts(
  content: string,
  variables: ReplyVariable[],
  literal: boolean,
  preview: boolean,
  L: Labels,
  sample: (key: string) => string,
): { text: string; variable: boolean }[] {
  const parts: { text: string; variable: boolean }[] = [];
  let offset = 0;
  if (!literal)
    for (const match of content.matchAll(tokens)) {
      if (match.index! > offset) parts.push({ text: content.slice(offset, match.index), variable: false });
      const key = match[1]!;
      const definition = variables.find((item) => item.key === key);
      const isAutomatic = definition?.kind === "AUTO" || (!definition && automatic(key));
      const label = isAutomatic ? L("REPLY_AUTO_" + key.toUpperCase()) : (definition?.label ?? key.replace(/_/g, " "));
      const value = sample(key);
      const text = preview
        ? isAutomatic
          ? value.trim()
            ? value
            : (definition?.fallback ?? "")
          : "[" + label + "]"
        : label;
      parts.push({ text, variable: true });
      offset = match.index! + match[0].length;
    }
  if (offset < content.length) parts.push({ text: content.slice(offset), variable: false });
  return parts;
}

/** Búsqueda inmediata sobre lo cargado (ReplySearchText); desde dos letras completa el servidor. */
export const needsServer = (term: string) => term.trim().length >= 2;
export const replyMatches = (item: ReplyView, term: string) =>
  !term.trim() ||
  contains(item.reply.name, term) ||
  contains(item.reply.shortcut, term) ||
  contains(item.reply.content, term) ||
  item.reply.variables.some((variable) => contains(variable.label, term));

/** El uso positivo en la fila (ReplyUsageText.ForList). */
export const usageForList = (value: ReplyView, L: Labels) =>
  value.sendsLast30Days !== null && value.sendsLast30Days > 0
    ? L.format(value.sendsLast30Days === 1 ? "A2_REPLY_USAGE_ONE" : "A2_REPLY_USAGE_MANY", value.sendsLast30Days)
    : "";
/** El uso con su última fecha en el detalle (ReplyUsageText.Format, fecha «g» de .NET). */
export function usageText(value: ReplyView, L: Labels, locale: string): string {
  if (value.sendsLast30Days === null) return L("REPLY_UNAVAILABLE");
  if (value.sendsLast30Days === 0) return L("A2_REPLY_NO_SENDS");
  const result = usageForList(value, L);
  return value.lastUsedAt
    ? result + " · " + L.format("A1_REPLY_LAST", shortDateTime(value.lastUsedAt, locale))
    : result;
}

/** Datos de ejemplo de la vista previa: el contacto es «Ana»; agente y empresa, los de la sesión. */
export function previewContext(bootstrap: ChatBootstrap | null): (key: string) => string {
  if (!bootstrap) return (key) => (key === "nombre" ? "Ana" : "");
  const person = bootstrap.people.find((item) => item.userId === bootstrap.userId);
  const display = person?.displayName?.trim() ? person.displayName : bootstrap.actorDisplayName;
  const first = display.split(/\s+/).filter(Boolean)[0] ?? "";
  return (key) =>
    key === "nombre" ? "Ana" : key === "agente" ? first : key === "empresa" ? bootstrap.organizationName : "";
}

export const fileUrl = (file: ReplyFile) => `/api/portal/chat/replies/files/${file.id}`;
const one = (value: number) => (Math.round(value * 10) / 10).toString();
export const fileSize = (bytes: number) =>
  bytes >= 1048576 ? `${one(bytes / 1048576)} MB` : `${one(bytes / 1024)} KB`;

// ---- API (HttpReplyDataSource) ----

/** Una caída o una respuesta inválida se informa como no disponible (HttpRequestException). */
export class ReplyUnavailable extends Error {}

async function read<T>(response: Response, allowFailure: boolean): Promise<T> {
  if (response.status >= 500) throw new ReplyUnavailable(String(response.status));
  if (!allowFailure && !response.ok) throw new ReplyUnavailable(String(response.status));
  try {
    const value = (await response.json()) as T;
    if (value === null || value === undefined) throw new Error("vacío");
    return value;
  } catch {
    throw new ReplyUnavailable("INVALID_REPLY_RESPONSE");
  }
}

export async function listReplies(
  search: string,
  scope: string,
  filter: string,
  offset: number,
  signal?: AbortSignal,
  options: { limit?: number; snapshotId?: string | null; snapshotStart?: boolean; activity?: boolean; activityAgeMs?: number } = {},
) {
  const params = new URLSearchParams({ search, scope, filter, inboxId: "", offset: String(offset), forUse: "False" });
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.snapshotId) params.set("snapshotId", options.snapshotId);
  if (options.snapshotStart) params.set("snapshotStart", "true");
  if (options.activity) params.set("activity", "true");
  if (options.activityAgeMs !== undefined) params.set("activityAgeMs", String(options.activityAgeMs));
  const response = await fetch(
    `/api/portal/chat/replies?${params}`,
    { credentials: "same-origin", signal },
  );
  return read<ReplyLibrary>(response, false);
}
/** La instantánea es temporal; el servidor valida de nuevo el actor y el antifalsificación. */
export async function releaseReplySnapshot(snapshotId: string, keepalive = false) {
  await fetch(`/api/portal/chat/replies/snapshots/${snapshotId}/release`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-CSRF-TOKEN": getAntiforgeryToken() ?? "" },
    keepalive,
  });
}
export async function readReply(id: string, signal?: AbortSignal) {
  return read<ReplyResult>(
    await fetch(`/api/portal/chat/replies/${id}`, { credentials: "same-origin", signal }),
    false,
  );
}
export async function changeReply(action: string, request: ReplyRequest, signal?: AbortSignal) {
  const response = await fetch(`/api/portal/chat/replies/${action}`, {
    method: "POST",
    credentials: "same-origin",
    signal,
    headers: { "Content-Type": "application/json", "X-CSRF-TOKEN": getAntiforgeryToken() ?? "" },
    body: JSON.stringify({ actionId: "00000000-0000-0000-0000-000000000000", version: 0, value: false, ...request }),
  });
  const result = await read<ReplyResult>(response, true);
  if (!response.ok && result.failureCode === null) throw new ReplyUnavailable("INVALID_REPLY_RESPONSE");
  if (response.ok && ["create", "update", "copy"].includes(action) && !result.value && !result.failureCode)
    throw new ReplyUnavailable("INVALID_REPLY_RESPONSE");
  return result;
}
export async function bootstrap(signal?: AbortSignal) {
  const response = await fetch("/api/portal/chat/bootstrap", { credentials: "same-origin", signal });
  return read<ChatBootstrap>(response, false);
}
